import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysDataSubscription } from '../queryKeys';

import { fetchExecutionLogDetail } from './fetchExecutionLogDetail';
import { type ExecutionLogWithHistory } from './validations';

/**
 * Generates the query options for the execution log detail query, e.g. for
 * loaders. Disabled via `skipToken` when no id is provided.
 *
 * @param logId - The id of the execution log entry to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export -- queryOptions are exported for loader reuse per the tanstack-query-hooks skill
export const getExecutionLogDetailQueryOptions = <
  TData = ExecutionLogWithHistory,
>({
  logId,
  options,
}: {
  logId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<ExecutionLogWithHistory, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: logId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchExecutionLogDetail(logId, { signal })
      : skipToken,
    queryKey: queryKeysDataSubscription.getExecutionLogDetail(logId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for a single execution log entry with its
 * version history. Disabled via `skipToken` when no id is provided.
 *
 * @param logId - The id of the execution log entry to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription
 */
export const useQueryExecutionLogDetail = <TData = ExecutionLogWithHistory>(
  logId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ExecutionLogWithHistory, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getExecutionLogDetailQueryOptions<TData>({ logId, options }));
